import { memo, useEffect, useMemo } from 'react';
import {
  TableFamiliarBodyVM,
  TableFamiliarVM,
  TableNewFamilySubjectsBodyVM,
} from 'practice/view-models/practice-edit-residence';
import { FormikProps } from 'formik';
import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCellsFamiliarBody } from '../constant';
import { noop } from 'shell/utils/noop';
import { HttpResponse } from 'shell/models/http-response';
import { AnprVM } from 'shell/view-models/search-anpr';
import { FamilyVM } from 'shell/view-models/family-member';
import { CadastralData, FamilyDatas, ResponseAnprData } from 'shell/models/person';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { transformFamilyMembers } from 'shell/utils/transform-family-members';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { transformSearchFamilyDataset } from 'shell/utils/transform-dataset-filter-list';
import { FormActualResidenceVM } from 'practice/view-models/pratice-actual-residence';
import { transformActualResidencePractice } from 'practice/utils/transform-residence-vm';
import { FamilyModalVM } from '..';

interface TableFamilyMembersProps {
  familyMembers?: Array<TableFamiliarBodyVM>;
  onClose: (open: boolean) => void;
  formContext: FormikProps<FamilyModalVM>;
  setOpenAccordionNewFamily: (value: boolean) => void;
}

const TableFamilyMembersComponent = ({
  familyMembers = [],
  formContext,
  setOpenAccordionNewFamily,
}: TableFamilyMembersProps): React.ReactElement => {
  const getAnpr = useHttpClient<
    HttpResponse<ResponseAnprData>,
    AnprVM<FamilyVM> & { datiFamigliaConvivenza?: FamilyDatas; datiCatastali?: CadastralData }
  >(`${__PERSON_API_URL__}/persona/pratiche/cerca/famiglia/aggregante`, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: [],
    },
    transformer: transformFamilyMembers,
  });

  const onChange = useEventCallback(
    (key: keyof TableFamiliarVM, value: TableFamiliarVM[keyof TableFamiliarVM]) => {
      formContext.setFieldValue('tableFamiliar', {
        ...(formContext.values.tableFamiliar || {}),
        [key]: value,
      });
    }
  );

  useEffect(() => {
    if (familyMembers?.length > 0) {
      onChange('body', familyMembers);
    }
  }, [familyMembers]);

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    formContext.setFieldValue('tableFamiliar', {
      ...(formContext.values.tableFamiliar || {}),
      page: 0,
      rowsPerPage,
    });
  });

  const onDetailPress = useEventCallback((item: TableFamiliarBodyVM) => {
    const data = transformSearchFamilyDataset({
      codeAnpr: item.idAnpr,
      page: 0,
      orders: {},
      rowsPerPage: 0,
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        AnprVM<FamilyVM> & { datiFamigliaConvivenza: FamilyDatas }
      >
    ).then((response) => {
      const castedResponse = response as AnprVM<FamilyVM> & {
        datiFamigliaConvivenza: FamilyDatas;
        datiCatastali?: CadastralData;
      };

      const residence: FormActualResidenceVM = transformActualResidencePractice(
        item.residenza,
        castedResponse?.datiCatastali,
        item.isHomeless
      );

      let newFamiliars: Array<TableNewFamilySubjectsBodyVM> = castedResponse.persons.map(
        (item1) => ({
          fiscalCode: item1.fiscalCode,
          surname: item1.surname,
          name: item1.name,
          gender: item1.gender,
          birthDate: item1.dateBirth,
          birthplace: item1.placeBirth,
          residence: item1.residence,
          relationshipValue: item1.relationshipCode,
          hideRelationship: true,
          residenza: item.residenza,
          subject3002: item1.subject3002,
          isNewFamily: true,
        })
      );

      const oldTable =
        formContext.values.tableNewFamilySubjects?.body
          ?.slice()
          ?.filter(
            (itemOld) =>
              item.fiscalCode !== itemOld.fiscalCode &&
              itemOld.posizionePersona?.toUpperCase() !== 'X' &&
              !itemOld.isNewFamily
          ) || [];

      newFamiliars = newFamiliars.concat(oldTable);

      const familiarsFiltered = item.isActive
        ? newFamiliars.filter((element) => !element.hideRelationship)
        : newFamiliars;

      const familiarsRelationship = (familiarsFiltered || []).map((itemFiltred) => {
        const isHeadParenthood = itemFiltred.hideRelationship
          ? false
          : !!familiarsFiltered?.find((item1) => {
              if (item1.fiscalCode !== itemFiltred.fiscalCode) {
                // non mostra intestatario se già presente dai soggetti con parentela disabilitata
                return item1.relationshipValue?.toString() === '1';
              }
              return false;
            });

        const isHeadMarriage = itemFiltred.hideRelationship
          ? false
          : !!familiarsFiltered?.find((item1) => {
              if (item1.fiscalCode !== itemFiltred.fiscalCode) {
                // non marito/moglie se già presente dai soggetti con parentela disabilitata
                return item1.relationshipValue?.toString() === '2';
              }
              return false;
            });

        return {
          ...itemFiltred,
          relationshipValue:
            isHeadParenthood || isHeadMarriage ? undefined : itemFiltred.relationshipValue,
        };
      });

      formContext.setValues({
        ...formContext.values,
        tableFamiliar: {
          ...(formContext.values.tableFamiliar || {}),
          body:
            formContext.values.tableFamiliar?.body?.map((familiar) =>
              familiar.fiscalCode === item.fiscalCode
                ? { ...familiar, isActive: !familiar.isActive }
                : { ...familiar, isActive: false }
            ) || [],
          item: item.isActive ? null : item,
        },
        tableNewFamilySubjects: {
          ...(formContext.values.tableNewFamilySubjects || {}),
          body: familiarsRelationship,
          residence,
          idFamigliaConvivenza:
            castedResponse?.datiFamigliaConvivenza?.famigliaConvivenza?.idFamigliaConvivenzaComune
              ?.idFamigliaConvivenza,
        },
      });

      // apertura accordion nuova famiglia
      setOpenAccordionNewFamily(true);
    });
  });

  const body: Array<TableFamiliarBodyVM> = useMemo(() => {
    if (!formContext.values.tableFamiliar?.body?.length) {
      return [];
    }

    return formContext.values.tableFamiliar?.body?.map((item, index) => ({
      ...item,
      action: (
        <FormControlLabel
          control={
            <Checkbox
              id={`${item.fiscalCode}-${index}-checkbox`}
              name={`${item.fiscalCode}-${index}-checkbox`}
              checked={item.isActive || false}
              onChange={() => onDetailPress(item)}
            />
          }
          label=""
        />
      ),
    }));
  }, [formContext.values.tableFamiliar?.body, onDetailPress]);

  return (
    <Grid container spacing={2}>
      {body.length > 0 ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista familiari"
            page={formContext.values.tableFamiliar?.page || 0}
            rowsPerPage={formContext.values.tableFamiliar?.rowsPerPage}
            body={body}
            count={body.length}
            head={headCellsFamiliarBody}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            onSort={noop}
            hideButtons
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

TableFamilyMembersComponent.displayName = 'Modal family';

const TableFamilyMembers = memo(TableFamilyMembersComponent);

export default TableFamilyMembers;
